import React, { useState, useCallback, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Power, MessageSquare, RefreshCw, X, CheckCircle, AlertCircle 
} from 'lucide-react';

import api from '../../api'; // Ensure this is correctly set up with auth headers
import { colors } from '../../styles/designSystem';
import { GradientButton } from '../ui/GradientButton';
import { Container } from '../ui/Container';
import { activatePrompt } from '../../api';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';


// Custom Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </button>
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
};

// Custom Alert Component
const AlertMessage = ({ type, message, duplicates }) => {
  const bgColor = type === 'success' ? 'bg-emerald-50' 
                : type === 'error' ? 'bg-red-50' 
                : type === 'warning' ? 'bg-yellow-50' 
                : 'bg-blue-50';
  const textColor = type === 'success' ? 'text-emerald-700' 
                  : type === 'error' ? 'text-red-700' 
                  : type === 'warning' ? 'text-yellow-700' 
                  : 'text-blue-700';
  const icon = type === 'success' ? <CheckCircle className="w-5 h-5 mr-2" /> 
             : type === 'error' ? <AlertCircle className="w-5 h-5 mr-2" /> 
             : type === 'warning' ? <AlertCircle className="w-5 h-5 mr-2" /> 
             : <CheckCircle className="w-5 h-5 mr-2" />;

  return (
    <div className={`${bgColor} ${textColor} p-4 rounded-xl flex items-start`}>
      {icon}
      <div>
        <p>{message}</p>
        {duplicates && (duplicates.duplicatesInBatch.length > 0 || duplicates.duplicatesInDatabase.length > 0) && (
          <ul className="mt-2 list-disc list-inside text-sm">
            {duplicates.duplicatesInBatch.length > 0 && (
              <>
                <li>Duplicate Numbers in Batch:</li>
                {duplicates.duplicatesInBatch.map((num, index) => (
                  <li key={`batch-${index}`}>{num}</li>
                ))}
              </>
            )}
            {duplicates.duplicatesInDatabase.length > 0 && (
              <>
                <li>Already Existing Numbers:</li>
                {duplicates.duplicatesInDatabase.map((num, index) => (
                  <li key={`db-${index}`}>{num}</li>
                ))}
              </>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

// VIP Number Row Component
const VipNumberRow = ({ vip, onEdit, onDelete }) => {
  // Debugging: Check the vip data structure
  // console.log('VIP Number Data:', vip);

  // Access the name directly from the populated promptId
  const associatedPromptName = vip.promptId ? vip.promptId.name : 'N/A';

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vip.phoneNumber}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{associatedPromptName}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <GradientButton 
          onClick={() => onEdit(vip)} 
          className="text-yellow-600 hover:text-yellow-800 mr-2"
        >
          <Edit2 className="w-4 h-4" />
        </GradientButton>
        <GradientButton 
          onClick={() => onDelete(vip._id)} 
          className="text-red-600 hover:text-red-800"
        >
          <Trash2 className="w-4 h-4" />
        </GradientButton>
      </td>
    </tr>
  );
};

const defaultPromptTemplate = `You are an assistant who schedules appointments for the company listed below. You must respond in language used by the customer. When the customer asks a question, reply in the language they use. Ensure that you guide the customer based only on the company's information provided below. First, ask for the customer's name and phone number. Once they provide these details, repeat them back to confirm before continuing. If the customer wishes to make an appointment, confirm the details (date and time) and finish with the phrase "Appointment confirmed" to indicate that the booking should be recorded. Respond as briefly as possible.`;

const initialFormState = {
  name: '',
  content: defaultPromptTemplate,
  threshold: 0.5,
  prefixPadding: 500,
  silenceDuration: 1000,
  phoneNumbers: '' // Added for multiple numbers
};

const PromptSettingsSection = ({ onLoading, onMessage, onError, onPromptActivation }) => {

  const { t } = useTranslation();
  const [prompts, setPrompts] = useState([]);
  const [vipNumbers, setVipNumbers] = useState([]);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [currentVip, setCurrentVip] = useState(null);
  const [alert, setAlert] = useState({ type: '', message: '', duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPromptTemplates();
    fetchVipNumbers();
  }, []);

  const fetchPromptTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_BASE_URL}/prompt-templates`);
      setPrompts(response.data);
    } catch (err) {
      onError(t('PromptSettingsSection.alerts.fetch_error'));
      console.error('Error fetching prompt templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVipNumbers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_BASE_URL}/vip-numbers`);
      setVipNumbers(response.data);
    } catch (err) {
      onError(t('PromptSettingsSection.alerts.fetch_vip_error'));
      console.error('Error fetching VIP numbers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'threshold' ? Math.min(1, Math.max(0, parseFloat(value))) : value
    }));
  };

  // Prompt Actions
  const handlePromptAction = useCallback(async (action, prompt = null) => {
    switch (action) {
      case 'add':
        setCurrentPrompt(null);
        setFormData(initialFormState);
        setShowPromptModal(true);
        break;
      case 'edit':
        setCurrentPrompt(prompt);
        setFormData(prompt);
        setShowPromptModal(true);
        break;
      case 'delete':
        if (window.confirm(t('PromptSettingsSection.alerts.delete_confirm'))) {
          try {
            setLoading(true);
            await api.delete(`${API_BASE_URL}/prompt-templates/${prompt._id}`);
            setAlert({ type: 'success', message: t('PromptSettingsSection.alerts.delete_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            await fetchPromptTemplates();
          } catch (err) {
            setAlert({ type: 'error', message: t('PromptSettingsSection.alerts.delete_error'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            console.error('Error deleting prompt template:', err);
          } finally {
            setLoading(false);
          }
        }
        break;
        case 'activate':
          if (prompt.isActive) {
            setAlert({ type: 'info', message: t('PromptSettingsSection.alerts.already_active'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            return;
          }
          try {
            setLoading(true);
            await activatePrompt(prompt._id, true);
            setAlert({ type: 'success', message: t('PromptSettingsSection.alerts.activate_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            await fetchPromptTemplates();
            // Add this line to notify parent component
            if (onPromptActivation) {
              onPromptActivation();
            }
          } catch (err) {
            setAlert({ type: 'error', message: t('PromptSettingsSection.alerts.activate_error'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            console.error('Error activating prompt template:', err);
          } finally {
            setLoading(false);
          }
          break;
      default:
        break;
    }
  }, [t,onPromptActivation]);

  const handlePromptSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };

      if (currentPrompt) {
        await api.put(`${API_BASE_URL}/prompt-templates/${currentPrompt._id}`, payload);
        setAlert({ type: 'success', message: t('PromptSettingsSection.alerts.update_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
      } else {
        await api.post(`${API_BASE_URL}/prompt-templates`, payload);
        setAlert({ type: 'success', message: t('PromptSettingsSection.alerts.create_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
      }

      setShowPromptModal(false);
      setFormData(initialFormState);
      await fetchPromptTemplates();
    } catch (err) {
      setAlert({ type: 'error', message: t('PromptSettingsSection.alerts.save_error'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
      console.error('Error saving prompt template:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePromptModalClose = () => {
    setShowPromptModal(false);
    setFormData(initialFormState);
  };

  // VIP Numbers Actions
  const handleVipAction = useCallback(async (action, vip = null) => {
    switch (action) {
      case 'add':
        setCurrentVip(null);
        setFormData({ ...initialFormState, phoneNumbers: '' }); // Reset phoneNumbers
        setShowVipModal(true);
        break;
      case 'edit':
        setCurrentVip(vip);
        setFormData({ 
          ...formData, 
          promptId: vip.promptId, 
          phoneNumbers: vip.phoneNumber // Single number for edit
        });
        setShowVipModal(true);
        break;
      case 'delete':
        if (window.confirm(t('PromptSettingsSection.vipNumbers.alerts.delete_confirm'))) {
          try {
            setLoading(true);
            await api.delete(`${API_BASE_URL}/vip-numbers/${vip}`);
            setAlert({ type: 'success', message: t('PromptSettingsSection.vipNumbers.alerts.delete_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            await fetchVipNumbers();
          } catch (err) {
            setAlert({ type: 'error', message: t('PromptSettingsSection.vipNumbers.alerts.delete_error'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
            console.error('Error deleting VIP number:', err);
          } finally {
            setLoading(false);
          }
        }
        break;
      default:
        break;
    }
  }, [t, formData]);

  const handleVipSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let payload = { 
        promptId: formData.promptId, 
        phoneNumbers: [] 
      };

      if (currentVip) {
        // Single update
        payload = { 
          promptId: formData.promptId, 
          phoneNumbers: [formData.phoneNumbers.trim()] 
        };
      } else {
        // Multiple add
        // Split the phoneNumbers string into an array
        const numbers = formData.phoneNumbers
          .split(/[\s,]+/)
          .map(num => num.trim())
          .filter(num => num !== '');
        payload.phoneNumbers = numbers;
      }

      if (currentVip) {
        // Update the single VIP number
        await api.put(`${API_BASE_URL}/vip-numbers/${currentVip._id}`, { promptId: payload.promptId, phoneNumber: payload.phoneNumbers[0] });
        setAlert({ type: 'success', message: t('PromptSettingsSection.vipNumbers.alerts.update_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
      } else {
        // Add multiple numbers
        const response = await api.post(`${API_BASE_URL}/vip-numbers/batch`, payload);
        
        const { insertedVips, duplicatesInBatch, duplicatesInDatabase } = response.data;

        if (duplicatesInBatch.length > 0 || duplicatesInDatabase.length > 0) {
          setAlert({ 
            type: 'warning', 
            message: 'Some phone numbers were duplicates and were not added.', 
            duplicates: { duplicatesInBatch, duplicatesInDatabase } 
          });
        } else {
          setAlert({ type: 'success', message: t('PromptSettingsSection.vipNumbers.alerts.create_success'), duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } });
        }
      }

      setShowVipModal(false);
      setFormData({ ...initialFormState, phoneNumbers: '' });
      await fetchVipNumbers();
    } catch (err) {
      // Check if the error response contains duplicates
      if (err.response && err.response.data) {
        const { message, duplicatesInBatch, duplicatesInDatabase } = err.response.data;
        setAlert({ 
          type: 'error', 
          message: message || t('PromptSettingsSection.vipNumbers.alerts.save_error'), 
          duplicates: { duplicatesInBatch: duplicatesInBatch || [], duplicatesInDatabase: duplicatesInDatabase || [] } 
        });
      } else {
        setAlert({ 
          type: 'error', 
          message: t('PromptSettingsSection.vipNumbers.alerts.save_error'), 
          duplicates: { duplicatesInBatch: [], duplicatesInDatabase: [] } 
        });
      }
      console.error('Error saving VIP numbers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVipModalClose = () => {
    setShowVipModal(false);
    setFormData({ ...initialFormState, phoneNumbers: '' });
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className={`${colors.text.heading} text-2xl font-semibold mb-2`}>
          {t('PromptSettingsSection.title')}
        </h2>
        <p className={`${colors.text.body}`}>
          {t('PromptSettingsSection.description')}
        </p>
      </div>

      {/* Alert Messages */}
      {alert.message && (
        <AlertMessage type={alert.type} message={alert.message} duplicates={alert.duplicates} />
      )}

      {/* Control Panels */}
      <div className="space-y-8">
        {/* Prompt Control Panel */}
        <div>
          <div className="flex justify-between items-center mb-4">
           
            <GradientButton
              onClick={() => handlePromptAction('add')}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('PromptSettingsSection.buttons.add_prompt')}
            </GradientButton>
          </div>

          {/* Prompts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prompts.map((prompt) => (
              <div key={prompt._id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <h3 className="text-lg font-medium">{prompt.name}</h3>
                    <div className={`mt-2 text-sm ${prompt.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                      {prompt.isActive ? t('PromptSettingsSection.prompt_card.status_active') : t('PromptSettingsSection.prompt_card.status_inactive')}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePromptAction('activate', prompt)}
                      className={`p-1 ${prompt.isActive ? 'text-green-600' : 'text-gray-400'} hover:text-green-600 transition-colors`}
                    >
                      <Power className="w-4 h-4" />
                    </button>
                    <GradientButton
                      onClick={() => handlePromptAction('edit', prompt)}
                      className="text-violet-600 hover:text-violet-800 p-1"
                    >
                      <Edit2 className="w-4 h-4" />
                    </GradientButton>
                    <GradientButton
                      onClick={() => handlePromptAction('delete', prompt)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </GradientButton>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  {prompt.content.length > 200 ? `${prompt.content.substring(0, 200)}...` : prompt.content}
                </p>
                {prompt.updatedAt && (
                  <div className="text-sm text-gray-500">
                    {t('PromptSettingsSection.prompt_card.last_updated')}: {new Date(prompt.updatedAt).toLocaleDateString()}
                  </div>
                )}
                <div className="mt-4 grid grid-cols-3 gap-4 text-sm text-gray-500">
                  <div>
                    <span className="font-medium">{t('PromptSettingsSection.modal.threshold_label')}:</span> {prompt.threshold}
                  </div>
                  <div>
                    <span className="font-medium">{t('PromptSettingsSection.modal.prefix_padding_label')}:</span> {prompt.prefixPadding}ms
                  </div>
                  <div>
                    <span className="font-medium">{t('PromptSettingsSection.modal.silence_duration_label')}:</span> {prompt.silenceDuration}ms
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {prompts.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('PromptSettingsSection.empty_state.title')}
              </h3>
              <p className="text-gray-500 mb-4">
                {t('PromptSettingsSection.empty_state.description')}
              </p>
              <GradientButton
                onClick={() => handlePromptAction('add')}
                className="inline-flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('PromptSettingsSection.empty_state.add_button')}
              </GradientButton>
            </div>
          )}
        </div>

        {/* VIP Numbers Control Panel */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`${colors.text.heading} text-xl font-semibold`}>
              {t('PromptSettingsSection.vipNumbers.title')}
            </h3>
            <GradientButton
              onClick={() => handleVipAction('add')}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('PromptSettingsSection.vipNumbers.add_vip')}
            </GradientButton>
          </div>

          {/* VIP Numbers Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('PromptSettingsSection.vipNumbers.table.phoneNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('PromptSettingsSection.vipNumbers.table.prompt')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('PromptSettingsSection.vipNumbers.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vipNumbers.map((vip) => (
                  <VipNumberRow
                    key={vip._id}
                    vip={vip}
                    onEdit={() => handleVipAction('edit', vip)}
                    onDelete={() => handleVipAction('delete', vip._id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {vipNumbers.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('PromptSettingsSection.vipNumbers.empty_state.title')}
              </h3>
              <p className="text-gray-500 mb-4">
                {t('PromptSettingsSection.vipNumbers.empty_state.description')}
              </p>
              <GradientButton
                onClick={() => handleVipAction('add')}
                className="inline-flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('PromptSettingsSection.vipNumbers.empty_state.add_button')}
              </GradientButton>
            </div>
          )}
        </div>
      </div>

      {/* Prompt Modal */}
      <Modal 
        isOpen={showPromptModal} 
        onClose={handlePromptModalClose} 
        title={currentPrompt ? t('PromptSettingsSection.modal.title_edit') : t('PromptSettingsSection.modal.title_add')}
      >
        <form onSubmit={handlePromptSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('PromptSettingsSection.modal.template_name_label')}
            </label>
            <input
              type="text"
              name="name"
              className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
              placeholder={t('PromptSettingsSection.modal.template_name_placeholder')}
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('PromptSettingsSection.modal.prompt_template_label')}
            </label>
            <textarea
              name="content"
              rows="5"
              className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
              placeholder={t('PromptSettingsSection.modal.prompt_template_placeholder')}
              value={formData.content}
              onChange={handleInputChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('PromptSettingsSection.modal.threshold_label')} ({formData.threshold})
            </label>
            <input
              type="range"
              name="threshold"
              min="0"
              max="1"
              step="0.1"
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              value={formData.threshold}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('PromptSettingsSection.modal.prefix_padding_label')} ({formData.prefixPadding}ms)
            </label>
            <input
              type="range"
              name="prefixPadding"
              min="0"
              max="2000"
              step="100"
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              value={formData.prefixPadding}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('PromptSettingsSection.modal.silence_duration_label')} ({formData.silenceDuration}ms)
            </label>
            <input
              type="range"
              name="silenceDuration"
              min="0"
              max="5000"
              step="100"
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              value={formData.silenceDuration}
              onChange={handleInputChange}
            />
          </div>

          <div className="flex justify-end space-x-4">
            <GradientButton 
              type="button" 
              onClick={handlePromptModalClose} 
              className="bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              {t('PromptSettingsSection.buttons.cancel')}
            </GradientButton>
            <GradientButton type="submit">
              {currentPrompt ? t('PromptSettingsSection.buttons.update') : t('PromptSettingsSection.buttons.create')}
            </GradientButton>
          </div>
        </form>
      </Modal>

      {/* VIP Numbers Modal */}
      <Modal 
        isOpen={showVipModal} 
        onClose={handleVipModalClose} 
        title={currentVip ? t('PromptSettingsSection.vipNumbers.modal.title_edit') : t('PromptSettingsSection.vipNumbers.modal.title_add')}
      >
        <form onSubmit={handleVipSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentVip ? t('PromptSettingsSection.vipNumbers.modal.phoneNumber_label') : 'Phone Numbers'}
            </label>
            {currentVip ? (
              <input
                type="tel"
                name="phoneNumbers"
                className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('PromptSettingsSection.vipNumbers.modal.phoneNumber_placeholder')}
                value={formData.phoneNumbers}
                onChange={(e) => setFormData({ ...formData, phoneNumbers: e.target.value })}
                required
              />
            ) : (
              <textarea
                name="phoneNumbers"
                rows="4"
                className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
                placeholder="Enter VIP phone numbers separated by commas, spaces, or newlines"
                value={formData.phoneNumbers}
                onChange={handleInputChange}
                required
              />
            )}
            {!currentVip && (
              <small className="text-gray-500">
                Enter multiple numbers separated by commas, spaces, or newlines.
              </small>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('PromptSettingsSection.vipNumbers.modal.prompt_label')}
            </label>
            <select
              name="promptId"
              className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
              value={formData.promptId}
              onChange={(e) => setFormData({ ...formData, promptId: e.target.value })}
              required
            >
              <option value="">{t('PromptSettingsSection.vipNumbers.modal.select_prompt')}</option>
              {prompts.map((prompt) => (
                <option key={prompt._id} value={prompt._id}>
                  {prompt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-4">
            <GradientButton 
              type="button" 
              onClick={handleVipModalClose} 
              className="bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              {t('PromptSettingsSection.vipNumbers.buttons.cancel')}
            </GradientButton>
            <GradientButton type="submit">
              {currentVip ? t('PromptSettingsSection.buttons.update_vip') : t('PromptSettingsSection.vipNumbers.buttons.add_vip')}
            </GradientButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PromptSettingsSection;
